import { useEffect, useState } from "react";

import { Anchor, RichText } from "@atlassiansox/bxp-content-framework";

import { useAnalytics } from "../../components";
import {
    UiPricingComparisonTableModel,
    UiPricingComparisonValueModel
} from "../../models";

import { SearchInput, Stack, Table } from "./UiPricingComparisonTable.styles";

type UiPricingComparisonTableProps = {
    model: UiPricingComparisonTableModel;
};

const UiPricingComparisonTable = ({ model }: UiPricingComparisonTableProps) => {
    const [currentSearch, setCurrentSearch] = useState("");

    const { sendTrackEvent } = useAnalytics();

    useEffect(() => {
        const timer = setTimeout(() => {
            if (currentSearch) {
                sendTrackEvent("searchedProductFeatures", {
                    event: "searched",
                    eventComponent: model.type,
                    eventContainer: "content",
                    label: model.content.title,
                    labelEnglish: model.content.entryTitle,
                    uuid: model.id
                });
            }
        }, 2000);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: add missing dependencies
    }, [currentSearch]);

    const {
        title,
        featureColumnHeader,
        freeColumnHeader,
        standardColumnHeader,
        premiumColumnHeader,
        enterpriseColumnHeader,
        searchFieldLabel,
        rows
    } = model.content;

    const td = (value: UiPricingComparisonValueModel, header?: string) => {
        return header ? (
            <td
                data-product={header}
                data-appearance={value.content.appearance}
            >
                <RichText document={value.content.value} />
            </td>
        ) : null;
    };

    return (
        <Stack>
            {searchFieldLabel && (
                <SearchInput
                    aria-label={searchFieldLabel}
                    onChange={(e) => setCurrentSearch(e.target.value)}
                    placeholder={searchFieldLabel}
                    type="search"
                    value={currentSearch}
                />
            )}

            <Table aria-label={title}>
                <thead>
                    <tr>
                        <th scope="col">{featureColumnHeader}</th>
                        {freeColumnHeader && (
                            <th scope="col">{freeColumnHeader}</th>
                        )}
                        {standardColumnHeader && (
                            <th scope="col">{standardColumnHeader}</th>
                        )}
                        {premiumColumnHeader && (
                            <th scope="col">{premiumColumnHeader}</th>
                        )}
                        {enterpriseColumnHeader && (
                            <th scope="col">{enterpriseColumnHeader}</th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {rows
                        .filter(
                            (row) =>
                                !currentSearch.length ||
                                row.content.title
                                    .toLowerCase()
                                    .includes(currentSearch.toLowerCase()) ||
                                row.content.lozenge
                                    ?.toLowerCase()
                                    .includes(currentSearch.toLowerCase()) ||
                                row.content.description
                                    ?.toLowerCase()
                                    .includes(currentSearch.toLowerCase())
                        )
                        .map(
                            (
                                {
                                    content: {
                                        title,
                                        lozenge,
                                        description,
                                        link,
                                        freeValue,
                                        standardValue,
                                        premiumValue,
                                        enterpriseValue
                                    }
                                },
                                index
                            ) => (
                                <tr key={index}>
                                    <th scope="row">
                                        <p>
                                            {title}
                                            {lozenge && <span>{lozenge}</span>}
                                        </p>
                                        {description && <p>{description}</p>}
                                        {link && (
                                            <p>
                                                <Anchor
                                                    href={link.content.url}
                                                    aria-label={
                                                        link.content.ariaLabel
                                                    }
                                                    onClick={() =>
                                                        sendTrackEvent(
                                                            "clickedUiLink",
                                                            {
                                                                event: "clicked",
                                                                eventComponent:
                                                                    "link",
                                                                eventContainer:
                                                                    model.type,
                                                                eventColumn:
                                                                    title,
                                                                label: link
                                                                    .content
                                                                    .text,
                                                                labelEnglish:
                                                                    link.content
                                                                        .entryTitle,
                                                                uuid: link.id,
                                                                href: link
                                                                    .content.url
                                                            }
                                                        )
                                                    }
                                                    data-uuid={link.id}
                                                    testId={link.id}
                                                >
                                                    {link.content.text}
                                                </Anchor>
                                            </p>
                                        )}
                                    </th>
                                    {td(freeValue, freeColumnHeader)}
                                    {td(standardValue, standardColumnHeader)}
                                    {td(premiumValue, premiumColumnHeader)}
                                    {td(
                                        enterpriseValue,
                                        enterpriseColumnHeader
                                    )}
                                </tr>
                            )
                        )}
                </tbody>
            </Table>
        </Stack>
    );
};

export default UiPricingComparisonTable;
